import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle,
  Button, Input, Badge,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/UI';
import MainLayout from '../Layout/MainLayout';
import apiClient from '../../utils/api';
import {
  Plus, Search, FileText, Clock, CheckCircle, AlertCircle,
  Mail, RefreshCw, Filter, ChevronRight, Play, Workflow
} from 'lucide-react';

const RFQ_STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-500', icon: FileText },
  created: { label: 'Created', color: 'bg-blue-500', icon: FileText },
  outreach: { label: 'Outreach', color: 'bg-purple-500', icon: Mail },
  bidding: { label: 'Bidding', color: 'bg-yellow-500', icon: Clock },
  closed: { label: 'Closed', color: 'bg-orange-500', icon: AlertCircle },
  analysis: { label: 'Analysis', color: 'bg-indigo-500', icon: RefreshCw },
  review: { label: 'Review', color: 'bg-pink-500', icon: FileText },
  awarding: { label: 'Awarding', color: 'bg-teal-500', icon: CheckCircle },
  awarded: { label: 'Awarded', color: 'bg-green-500', icon: CheckCircle },
  po_created: { label: 'PO Created', color: 'bg-green-600', icon: CheckCircle },
  completed: { label: 'Completed', color: 'bg-green-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-500', icon: AlertCircle }
};

const WORKFLOW_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-500' },
  running: { label: 'Running', color: 'bg-blue-500' },
  waiting_for_user: { label: 'Waiting', color: 'bg-yellow-500' },
  completed: { label: 'Completed', color: 'bg-green-500' },
  error: { label: 'Error', color: 'bg-red-500' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-400' }
};

const RFQWorkflowDashboard = () => {
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState([]);
  const [workflowRuns, setWorkflowRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('rfqs');
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    awaiting: 0,
    completed: 0
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchRFQs(), fetchWorkflowRuns()]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRFQs = async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await apiClient.get('/api/rfq-workflow/rfqs', { params });

      if (response && response.rfqs) {
        setRfqs(response.rfqs);
        calculateStats(response.rfqs);
      }
    } catch (error) {
      console.error('Error fetching RFQs:', error);
    }
  };

  const fetchWorkflowRuns = async () => {
    try {
      const response = await apiClient.get('/api/rfq-workflow/runs', {
        params: { workflow_id: 25, limit: 50 }
      });

      if (response && response.runs) {
        setWorkflowRuns(response.runs);
      }
    } catch (error) {
      console.error('Error fetching workflow runs:', error);
    }
  };

  const calculateStats = (rfqList) => {
    setStats({
      total: rfqList.length,
      active: rfqList.filter(r => ['outreach', 'bidding', 'analysis'].includes(r.status)).length,
      awaiting: rfqList.filter(r => ['review', 'awarding'].includes(r.status)).length,
      completed: rfqList.filter(r => ['completed', 'po_created', 'awarded'].includes(r.status)).length
    });
  };

  const startNewWorkflow = async () => {
    try {
      const response = await apiClient.post('/api/rfq-workflow/start', {
        workflow_id: 25
      });

      if (response && response.run_id) {
        navigate(`/rfq-workflow/run/${response.run_id}`);
      }
    } catch (error) {
      console.error('Error starting workflow:', error);
      alert('Failed to start workflow: ' + (error.message || 'Unknown error'));
    }
  };

  const filteredRFQs = rfqs.filter(rfq => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      rfq.rfq_number?.toLowerCase().includes(term) ||
      rfq.title?.toLowerCase().includes(term)
    );
  });

  const filteredRuns = workflowRuns.filter(run => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      run.run_id?.toLowerCase().includes(term) ||
      run.workflow_name?.toLowerCase().includes(term) ||
      run.current_step?.toLowerCase().includes(term)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getStatusBadge = (status, config) => {
    const statusConfig = config[status] || { label: status, color: 'bg-gray-500' };
    const Icon = statusConfig.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.color} text-white`}>
        {Icon && <Icon className="w-3 h-3" />}
        {statusConfig.label}
      </span>
    );
  };

  return (
    <MainLayout title="RFQ Workflow Dashboard">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              RFQ Workflow Dashboard
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              Workflow-based RFQ management with automated processing
            </p>
          </div>
          <Button
            onClick={startNewWorkflow}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Play className="w-4 h-4 mr-2" />
            Start New RFQ Workflow
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total RFQs</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.active}</p>
                </div>
                <RefreshCw className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Awaiting Action</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.awaiting}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'rfqs'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('rfqs')}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            RFQs
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'workflows'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('workflows')}
          >
            <Workflow className="w-4 h-4 inline mr-2" />
            Workflow Runs
          </button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4 items-center">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder={activeTab === 'rfqs' ? "Search RFQs..." : "Search workflows..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              {activeTab === 'rfqs' && (
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-48">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="bidding">Bidding</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="review">Review</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <Button variant="outline" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Content */}
        {activeTab === 'rfqs' ? (
          <Card>
            <CardHeader>
              <CardTitle>Request for Quotations</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : filteredRFQs.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No RFQs found</p>
                  <Button
                    variant="link"
                    onClick={startNewWorkflow}
                    className="mt-2"
                  >
                    Start your first RFQ workflow
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>RFQ Number</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>End Date</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRFQs.map((rfq) => (
                      <TableRow
                        key={rfq.id}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                        onClick={() => navigate(`/rfq-workflow/rfq/${rfq.id}`)}
                      >
                        <TableCell className="font-medium">
                          {rfq.rfq_number}
                        </TableCell>
                        <TableCell>{rfq.title || '-'}</TableCell>
                        <TableCell>{getStatusBadge(rfq.status, RFQ_STATUS_CONFIG)}</TableCell>
                        <TableCell>{rfq.customer_name || '-'}</TableCell>
                        <TableCell>{formatDate(rfq.end_date)}</TableCell>
                        <TableCell>{formatDate(rfq.created_at)}</TableCell>
                        <TableCell>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Workflow Runs</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : filteredRuns.length === 0 ? (
                <div className="text-center py-8">
                  <Workflow className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No workflow runs found</p>
                  <Button
                    variant="link"
                    onClick={startNewWorkflow}
                    className="mt-2"
                  >
                    Start your first RFQ workflow
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Run ID</TableHead>
                      <TableHead>Workflow</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Current Step</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Last Updated</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRuns.map((run) => (
                      <TableRow
                        key={run.run_id}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                        onClick={() => navigate(`/rfq-workflow/run/${run.run_id}`)}
                      >
                        <TableCell className="font-medium font-mono text-sm">
                          {run.run_id?.substring(0, 8)}...
                        </TableCell>
                        <TableCell>{run.workflow_name || 'RFQ Workflow'}</TableCell>
                        <TableCell>
                          {getStatusBadge(run.status, WORKFLOW_STATUS_CONFIG)}
                        </TableCell>
                        <TableCell className="text-gray-600 dark:text-gray-400">
                          {run.current_step || '-'}
                        </TableCell>
                        <TableCell>{formatDateTime(run.created_at)}</TableCell>
                        <TableCell>{formatDateTime(run.updated_at)}</TableCell>
                        <TableCell>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default RFQWorkflowDashboard;
